import { motion } from "framer-motion";
import { TrendingUp, Building2, Landmark } from "lucide-react";
import { INDIA_VIEWBOX, INDIA_MAINLAND, INDIA_ISLANDS } from "./indiaOutline";

/**
 * HeroIllustration — "capital corridor" vector.
 *
 * India (official Survey of India outline, simplified — see indiaOutline.js)
 * receives inbound capital arcs from the six markets where most NRIs live.
 * Packets travel each arc, the border draws itself on load, and three chips
 * label the Indian rails the money lands in.
 */

// Currencies per market for the label badges
const MARKET_CURRENCIES = {
  "New York": { code: "USD", color: "#16a34a", bg: "#dcfce7" },
  "London":   { code: "GBP", color: "#1d4ed8", bg: "#dbeafe" },
  "Dubai":    { code: "AED", color: "#b45309", bg: "#fef3c7" },
  "Toronto":  { code: "CAD", color: "#dc2626", bg: "#fee2e2" },
  "Singapore":{ code: "SGD", color: "#0891b2", bg: "#cffafe" },
  "Sydney":   { code: "AUD", color: "#7c3aed", bg: "#ede9fe" },
};
// to leave room for the surrounding market nodes.
const MAP_SCALE = 0.75;
const MAP_TRANSFORM = `translate(240 260) scale(${MAP_SCALE}) translate(-240 -260)`;

// Central India (~79°E, 22.5°N) in canvas coords, after the scale above.
const HUB = { x: 195, y: 252 };

const MARKETS = [
  { label: "New York", x: 40, y: 70, dur: 7.5, delay: 0 },
  { label: "London", x: 418, y: 52, dur: 8.5, delay: 0.9 },
  { label: "Dubai", x: 452, y: 232, dur: 7, delay: 1.8 },
  { label: "Toronto", x: 26, y: 214, dur: 9, delay: 2.6 },
  { label: "Singapore", x: 428, y: 452, dur: 8, delay: 3.4 },
  { label: "Sydney", x: 58, y: 482, dur: 9.5, delay: 4.2 },
];

// Quadratic arc from a market into the hub, bowed away from the straight line.
function arcTo(m) {
  const mx = (m.x + HUB.x) / 2;
  const my = (m.y + HUB.y) / 2;
  const bow = 44;
  const cx = mx + (m.y < HUB.y ? bow : -bow) * 0.55;
  const cy = my - bow;
  return `M${m.x} ${m.y} Q${cx} ${cy} ${HUB.x} ${HUB.y}`;
}

const RAILS = [
  { icon: TrendingUp, label: "Equity & funds", pos: "left-0 top-[4%]" },
  { icon: Building2, label: "GIFT City", pos: "right-0 top-[48%]" },
  { icon: Landmark, label: "NRE / NRO", pos: "left-[1%] bottom-[3%]" },
];

/**
 * Indian financial hubs, plotted from real coordinates through the same
 * projection used to generate the map, then through MAP_TRANSFORM.
 * `key` marks the venues we label; the rest render as quiet secondary dots.
 */
const HUBS = [
  { name: "Delhi NCR", x: 166, y: 165, key: true, anchor: "start" },
  { name: "GIFT City", x: 105, y: 241, key: true, anchor: "end", pulse: true },
  { name: "Mumbai", x: 109, y: 301, key: true, anchor: "end", pulse: true },
  { name: "Hyderabad", x: 183, y: 325, key: false },
  { name: "Bengaluru", x: 171, y: 388, key: true, anchor: "end" },
  { name: "Chennai", x: 207, y: 386, key: false },
];

export default function HeroIllustration({ className = "" }) {
  return (
    <div className={`relative w-full max-w-[620px] mx-auto aspect-square ${className}`} data-testid="hero-illustration">
      {/* Accent glow behind the map */}
      <div
        aria-hidden
        className="absolute inset-[18%] rounded-full opacity-40 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle at 45% 45%, #fb923c 0%, #16a34a 60%, transparent 80%)" }}
      />

      <svg
        viewBox={INDIA_VIEWBOX}
        className="absolute inset-0 w-full h-full"
        role="img"
        aria-label="Capital flowing from global markets into India"
      >
        <defs>
          <linearGradient id="indiaStroke" x1="0" y1="0" x2="0.4" y2="1">
            <stop offset="0%" stopColor="#c45e00" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#1a6b5a" stopOpacity="0.85" />
          </linearGradient>
          <linearGradient id="indiaFill" x1="0" y1="0" x2="0.5" y2="1">
            <stop offset="0%"   stopColor="#f97316" />
            <stop offset="35%"  stopColor="#fb923c" />
            <stop offset="70%"  stopColor="#fdba74" />
            <stop offset="100%" stopColor="#16a34a" />
          </linearGradient>
          <radialGradient id="hubPulse" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#f97316" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
          </radialGradient>
          <pattern id="mapDots" width="13" height="13" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.4" fill="#7c2d00" fillOpacity="0.18" />
          </pattern>
          <clipPath id="indiaClip">
            <path d={INDIA_MAINLAND} transform={MAP_TRANSFORM} />
          </clipPath>
          {/* Sheen overlay — lighter highlight toward top-left */}
          <radialGradient id="indiaSheen" cx="30%" cy="25%" r="60%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
          {/* Sparkline glow filter */}
          <filter id="sparkGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          {/* Area fill under sparkline */}
          <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#22c55e" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* ── Growth sparkline — rendered BEFORE India so it sits behind ── */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.82 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          {/* Soft area fill under the line */}
          <motion.path
            d="M 60 430 L 90 400 L 118 415 L 148 385 L 172 398 L 200 362 L 226 378 L 256 340 L 278 355 L 308 310 L 308 440 Z"
            fill="url(#sparkFill)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
          />
          {/* Zigzag sparkline — draws itself on */}
          <motion.polyline
            points="60,430 90,400 118,415 148,385 172,398 200,362 226,378 256,340 278,355 308,310"
            fill="none"
            stroke="#22c55e"
            strokeWidth="3.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#sparkGlow)"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 2, delay: 1.2, ease: "easeOut" }}
          />
          {/* Arrowhead — solid filled triangle pointing up-right at the tip */}
          <motion.path
            d="M 308 310 L 293 326 L 302 315 L 291 308 Z"
            fill="#22c55e"
            filter="url(#sparkGlow)"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 3.3 }}
            style={{ transformOrigin: "300px 317px" }}
          />
          {/* Pulsing tip dot */}
          <circle cx="308" cy="310" r="4" fill="#22c55e" filter="url(#sparkGlow)">
            <animate attributeName="r" values="4;7;4" dur="2s" repeatCount="indefinite" begin="3.4s" />
            <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite" begin="3.4s" />
          </circle>
        </motion.g>

        {/* Orbit rings */}
        {[150, 196, 240].map((r, i) => (
          <motion.circle
            key={r}
            cx={HUB.x}
            cy={HUB.y}
            r={r}
            fill="none"
            stroke="hsl(32 98% 48%)"
            strokeOpacity={0.10 - i * 0.02}
            strokeWidth="1"
            strokeDasharray="3 9"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 + i * 0.12, ease: "easeOut" }}
            style={{ transformOrigin: `${HUB.x}px ${HUB.y}px` }}
          />
        ))}

        <circle cx={HUB.x} cy={HUB.y} r="132" fill="url(#hubPulse)" />

        {/* India */}
        <motion.g
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Solid gradient fill directly on the mainland shape */}
          <path
            d={INDIA_MAINLAND}
            transform={MAP_TRANSFORM}
            fill="url(#indiaFill)"
            stroke="none"
          />

          {/* Sheen highlight */}
          <g clipPath="url(#indiaClip)">
            <rect x="0" y="0" width="480" height="520" fill="url(#indiaSheen)" />
            <rect x="0" y="0" width="480" height="520" fill="url(#mapDots)" />
          </g>

          {/* Mainland border, drawn on top */}
          <motion.path
            d={INDIA_MAINLAND}
            transform={MAP_TRANSFORM}
            fill="none"
            stroke="url(#indiaStroke)"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2.4, delay: 0.25, ease: "easeInOut" }}
          />

          {/* ₹ symbol — bold watermark over the map */}
          <motion.text
            x="190"
            y="265"
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="82"
            fontWeight="900"
            fontFamily="'Sora', system-ui, sans-serif"
            fill="#7c2d12"
            fillOpacity="0.22"
            filter="url(#sparkGlow)"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 1.8, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: "190px 265px" }}
          >
            ₹
          </motion.text>

          {/* Island territories (Andaman & Nicobar, Lakshadweep) */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.9 }}
          >
            {INDIA_ISLANDS.map((d) => (
              <path
                key={d}
                d={d}
                transform={MAP_TRANSFORM}
                fill="#16a34a"
                fillOpacity="0.75"
                stroke="#c45e00"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </motion.g>
        </motion.g>

        {/* Inbound capital arcs */}
        {MARKETS.map((m, i) => {
          const d = arcTo(m);
          const cur = MARKET_CURRENCIES[m.label];
          return (
            <g key={m.label}>
              <motion.path
                d={d}
                fill="none"
                stroke={cur.color}
                strokeOpacity="0.55"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeDasharray="5 8"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.1, delay: 0.5 + i * 0.1, ease: "easeOut" }}
              />
              <circle r="4" fill={cur.color} opacity="0.9">
                <animateMotion dur={`${m.dur}s`} repeatCount="indefinite" begin={`${m.delay}s`} path={d} />
                <animate
                  attributeName="opacity"
                  values="0;0.9;0.9;0"
                  dur={`${m.dur}s`}
                  repeatCount="indefinite"
                  begin={`${m.delay}s`}
                />
              </circle>
              <motion.circle
                cx={m.x}
                cy={m.y}
                r="6"
                fill={cur.bg}
                stroke={cur.color}
                strokeWidth="2"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.6 + i * 0.1 }}
                style={{ transformOrigin: `${m.x}px ${m.y}px` }}
              />
            </g>
          );
        })}

        {/* Hub marker */}
        <circle cx={HUB.x} cy={HUB.y} r="6.5" fill="#f97316" />
        <circle cx={HUB.x} cy={HUB.y} r="6.5" fill="none" stroke="#f97316" strokeWidth="1.5" opacity="0.6">
          <animate attributeName="r" values="6.5;22;6.5" dur="2.8s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.6;0;0.6" dur="2.8s" repeatCount="indefinite" />
        </circle>
      </svg>

      {/* Market labels — city + currency badge */}
      {MARKETS.map((m, i) => {
        const cur = MARKET_CURRENCIES[m.label];
        return (
          <motion.div
            key={m.label}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-1.5 rounded-lg bg-white border border-white/80 px-2.5 py-1.5 whitespace-nowrap"
            style={{
              left: `${(m.x / 480) * 100}%`,
              top: `${((m.y - 24) / 520) * 100}%`,
              boxShadow: `0 2px 8px rgba(0,0,0,0.10), 0 0 0 1px ${cur.color}22`,
            }}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.8 + i * 0.09 }}
          >
            <span className="font-heading text-[10px] font-semibold text-[#374151]">{m.label}</span>
            <span
              className="font-heading text-[9.5px] font-bold px-1.5 py-0.5 rounded-md"
              style={{ color: cur.color, background: cur.bg }}
            >
              {cur.code}
            </span>
          </motion.div>
        );
      })}

      {/* Rail chips — vivid coloured cards */}
      {RAILS.map(({ icon: Icon, label, pos }, i) => {
        const chipColors = [
          { bg: "#fff7ed", border: "#f97316", icon: "#ea580c", text: "#9a3412" },
          { bg: "#f0fdf4", border: "#16a34a", icon: "#16a34a", text: "#14532d" },
          { bg: "#eff6ff", border: "#3b82f6", icon: "#2563eb", text: "#1e3a8a" },
        ];
        const c = chipColors[i];
        return (
          <motion.div
            key={label}
            className={`absolute ${pos} inline-flex items-center gap-2 rounded-xl px-3 py-2 border`}
            style={{
              background: c.bg,
              borderColor: c.border,
              boxShadow: `0 2px 12px ${c.border}30`,
            }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.2 + i * 0.14 }}
          >
            <Icon size={14} style={{ color: c.icon }} />
            <span className="font-heading text-[11.5px] font-bold" style={{ color: c.text }}>{label}</span>
          </motion.div>
        );
      })}
    </div>
  );
}
